import { useState } from "react";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  Check,
  X,
  MessageCircle,
  Facebook,
  Twitter,
  Mail as MailIcon,
  Download,
} from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * Share-your-Buddy modal. Turns each generated Buddy into a viral share.
 *
 *   variant="icon" → small round button (top-right of card / result screen)
 *   variant="pill" → full pill "Share your Buddy" CTA
 *
 * Uses the native Web Share API when available (mobile), and falls back to
 * explicit share links (WhatsApp, Messenger, Facebook, X/Twitter, Email, Copy).
 * Always deep-links to /buddies/:id/share — a public page that renders with
 * the buddy image + a "make your own free" CTA for the recipient.
 */
export default function ShareBuddyButton({ buddy, variant = "icon", className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/buddies/${buddy?.id}/share`
      : `https://platebuddies.com/buddies/${buddy?.id}/share`;
  const shareTitle = buddy?.name
    ? `Meet ${buddy.name} — my kid's own Plate Buddy!`
    : "Meet my kid's Plate Buddy!";
  const shareText = buddy?.name
    ? `${buddy.name} — my kid designed their very own food character. Make one free 👇`
    : "My kid designed their very own food character. Make one free 👇";

  const openModal = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    trackEvent("share_open", { buddy_id: buddy?.id, buddy_name: buddy?.name });

    // Prefer native Web Share on mobile — much smoother, one tap to Messages/WhatsApp/etc.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        trackEvent("share_native_success", { buddy_id: buddy?.id });
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to modal.
        if (err?.name === "AbortError") return;
      }
    }
    setOpen(true);
  };

  const closeModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen(false);
    setCopied(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackEvent("share_copy", { buddy_id: buddy?.id });
      toast.success("Link copied — paste it anywhere!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually.");
    }
  };

  const downloadImage = async () => {
    try {
      if (!buddy?.image) return;
      const link = document.createElement("a");
      link.href = buddy.image;
      link.download = `${(buddy.name || "plate-buddy").toLowerCase().replace(/\s+/g, "-")}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      trackEvent("share_download_image", { buddy_id: buddy?.id });
      toast.success("Buddy image saved!");
    } catch {
      toast.error("Couldn't download that image. Try right-click → Save as.");
    }
  };

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const emailBody = encodeURIComponent(`${shareText}\n\n${shareUrl}`);

  const shareLinks = [
    {
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      color: "#25D366",
      testid: "share-whatsapp",
    },
    {
      label: "Messages",
      icon: MessageCircle,
      href: `sms:?body=${encodedText}%20${encodedUrl}`,
      color: "#4CD964",
      testid: "share-sms",
    },
    {
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "#1877F2",
      testid: "share-facebook",
    },
    {
      label: "X / Twitter",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      color: "#000000",
      testid: "share-twitter",
    },
    {
      label: "Email",
      icon: MailIcon,
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${emailBody}`,
      color: "#F26A34",
      testid: "share-email",
    },
  ];

  const IconBtn = (
    <button
      type="button"
      data-testid={`btn-share-buddy-${buddy?.id || "new"}`}
      onClick={openModal}
      aria-label={`Share ${buddy?.name || "your Buddy"}`}
      className={`h-10 w-10 rounded-full bg-white/95 backdrop-blur shadow-md flex items-center justify-center hover:bg-pb-coral hover:text-white text-pb-ink transition-all duration-200 active:scale-95 ${className}`}
    >
      <Share2 className="h-4 w-4" strokeWidth={2.5} />
    </button>
  );

  const PillBtn = (
    <button
      type="button"
      data-testid={`btn-share-buddy-${buddy?.id || "new"}`}
      onClick={openModal}
      className={`inline-flex items-center gap-2 px-5 py-3 rounded-full bg-pb-ink text-white font-black text-sm shadow-lg hover:bg-pb-coral hover:shadow-xl transition-all duration-200 active:scale-[0.98] ${className}`}
    >
      <Share2 className="h-4 w-4" />
      Share {buddy?.name || "your Buddy"}
    </button>
  );

  return (
    <>
      {variant === "pill" ? PillBtn : IconBtn}
      {open && (
        <div
          data-testid="share-buddy-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="absolute inset-0 bg-pb-ink/60 backdrop-blur-sm animate-fade-in"
            aria-hidden="true"
          />
          <div
            className="relative bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              data-testid="btn-share-buddy-close"
              aria-label="Close"
              className="absolute top-4 right-4 h-8 w-8 rounded-full text-pb-soft hover:bg-pb-cream flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pb-coral/10 text-pb-coral text-[11px] font-black uppercase tracking-widest">
              <Share2 className="h-3 w-3" /> Share
            </div>
            <h3 className="font-display font-black text-2xl text-pb-ink mt-3 leading-tight">
              Show off {buddy?.name ? <span className="text-pb-coral">{buddy.name}</span> : "your Buddy"}
            </h3>
            <p className="mt-2 text-pb-soft text-sm leading-relaxed">
              Send it to grandparents, group chats, or your feed — each person you share with can design their own free.
            </p>

            {/* Copy-link bar */}
            <div className="mt-5 flex items-center gap-2 rounded-2xl border-2 border-pb-cream bg-pb-cream px-3 py-2.5">
              <input
                readOnly
                value={shareUrl}
                data-testid="share-buddy-url"
                aria-label="Share URL"
                className="flex-1 min-w-0 bg-transparent text-xs font-semibold text-pb-ink truncate focus:outline-none"
                onClick={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                data-testid="btn-share-copy"
                onClick={copyLink}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold transition ${
                  copied
                    ? "bg-pb-green/20 text-pb-green"
                    : "bg-pb-ink text-white hover:bg-pb-coral"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </>
                )}
              </button>
            </div>

            {/* Share-channel grid */}
            <div className="mt-5 grid grid-cols-3 gap-2">
              {shareLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target={s.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  onClick={() =>
                    trackEvent("share_channel_click", {
                      channel: s.label,
                      buddy_id: buddy?.id,
                    })
                  }
                  className="flex flex-col items-center gap-1.5 rounded-2xl p-3 border-2 border-pb-cream hover:border-pb-coral hover:bg-pb-cream transition group"
                >
                  <span
                    className="h-9 w-9 rounded-full flex items-center justify-center text-white transition-transform group-hover:scale-110"
                    style={{ background: s.color }}
                  >
                    <s.icon className="h-4 w-4" />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-wider text-pb-ink">
                    {s.label}
                  </span>
                </a>
              ))}
              {buddy?.image && (
                <button
                  type="button"
                  onClick={downloadImage}
                  data-testid="btn-share-download-image"
                  className="flex flex-col items-center gap-1.5 rounded-2xl p-3 border-2 border-pb-cream hover:border-pb-coral hover:bg-pb-cream transition group"
                >
                  <span
                    className="h-9 w-9 rounded-full flex items-center justify-center text-white transition-transform group-hover:scale-110 bg-pb-sun"
                  >
                    <Download className="h-4 w-4 text-pb-ink" />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-wider text-pb-ink">
                    Download
                  </span>
                </button>
              )}
            </div>

            <p className="mt-5 text-[11px] text-pb-soft text-center leading-relaxed">
              Anyone who opens the link can design their own free Plate Buddy 🎨
            </p>
          </div>
        </div>
      )}
    </>
  );
}
